export const frontmatter = {
  title: "О проекте",
  slug: "about",
  category: "О проекте",
  reading_minutes: 3,
  updated: "2026-04-29",
  abstract: "Зачем сделана грибная карта Ленобласти, что на ней работает сегодня и где она честно бессильна.",
};

import { Callout, KeyFacts, Stats, Steps, TLDR } from "../../components/mdx";

# О проекте

<TLDR>
**Geobiom** — попытка собрать разрозненные данные о лесах Ленобласти в одну интерактивную карту. Делает один человек как хобби: без команды, без инвестиций, без монетизации.
</TLDR>

## Зачем

В рунете есть два полюса «грибных» ресурсов: форумы с личным опытом сборщиков, но без структурированных данных, и мобильные приложения, распознающие гриб по фото (и ошибающиеся на опасных двойниках). Ни там, ни там нет ответа на базовый вопрос — *«где у меня под боком растёт то, что я ищу»*. Карта с лесохозяйственными данными Рослесхоза, уточнённая OSM и Copernicus DEM, закрывает именно эту дыру.

## Что сейчас работает

<KeyFacts facts={[
  { label: "Лесные полигоны", value: "~2,17M выделов из ФГИС ЛК Рослесхоза, подкрашены по доминирующей породе" },
  { label: "В попапе", value: "бонитет, возрастная группа, запас древесины, формула смешения пород" },
  { label: "Слои сверху", value: "ООПТ, дороги, водоёмы, заболоченные участки, рельеф (hillshade)" },
  { label: "По клику", value: "теоретически возможные грибные виды — справочник, не «здесь точно растёт»" },
  { label: "Справочник видов", value: "18 видов — placeholder; полное наполнение 24 видов в Phase 3" },
]} />

## Ограничения — читайте

<Callout type="warn" title="Рослесхоз не всегда свежий">
Часть выделов не обновлялась с 2015 года, и молодой лес 10-летней давности может в данных выглядеть как «гарь». Это конкретно влияет на то, какие грибные виды предлагает карта.
</Callout>

<Callout type="warn" title="Соответствие порода → грибы экспертное">
Мы не считали фактические находки; мы утверждаем, что с сосной ассоциирован масленок, с берёзой — подберёзовик. Это честный «общий смысл», не измеренное число.
</Callout>

<Callout type="warn" title="Координат конкретных находок нет">
Данные ВК-групп агрегируются до района, чтобы не создавать соблазна вытаптывать чью-то полянку. Если нужна точка — ходите и ищите сами.
</Callout>

<Callout type="warn" title="Никакой алгоритм не скажет, съедобен ли гриб">
Мы вообще не определяем грибы по фото. Определение — ответственность собирающего, и это не преувеличение: читайте раздел про безопасность в [условиях использования](/legal/terms).
</Callout>

## Что дальше

Полный список планов и идей — в [roadmap на GitHub](https://github.com/yungkocherov/mushroom_map/blob/main/docs/roadmap_content_ideas.md). Ближайшие фазы: наполнение справочника видов, прогноз плодоношения по районам (модель в отдельном репозитории `mushroom-forecast`), офлайн-карта в мобильном браузере (PWA).

## Технологии

PostGIS + FastAPI на бэкенде; React + MapLibre GL + PMTiles на фронте. Данные — в одной Postgres-базе (~2 млн лесных полигонов), тайлы — как один pmtiles-файл с HTTP Range-запросами. Код полностью открытый; лицензия — в репозитории.

## Обратная связь

Баг, замечание, идея, несогласие — через [issues на GitHub](https://github.com/yungkocherov/mushroom_map/issues) или почтой [ikocherov@mail.ru](mailto:ikocherov@mail.ru). Отвечаю не всегда быстро, но читаю всё.
